/**
 * Formatting — money, time, measurement, score, and the honesty badges.
 *
 * ── Why this file exists ─────────────────────────────────────────────────────
 *
 * Three rules in this project are only enforceable if there is exactly one
 * place that turns a stored value into something a person reads:
 *
 *  1. **Money is integer cents.** The single division by 100 lives in
 *     `shared/money.ts` and this file never does arithmetic on a dollar
 *     figure — it hands cents to `centsToDisplay` and renders what comes back.
 *  2. **Dates come from `shared/time.ts`.** "Today" is today in
 *     America/Puerto_Rico. A UTC date rolls over at 20:00 local, which is how
 *     an action due today reads as overdue while the afternoon is still going.
 *  3. **An estimate must never render as a fact.** The certainty badges, the
 *     cost-confidence band and the word "assumed" are all defined here, once,
 *     so no screen can quietly present an AI inference in the same type as a
 *     photographed title.
 *
 * Every function takes a `Locale`. Nothing here reads the ambient locale —
 * `t()` is called during render (i18n.ts's house rule) and a formatter that
 * captured the locale at module scope would freeze at whichever language the
 * bundle first loaded under, which is the exact bug `Admin.tsx` has.
 *
 * ── Strings ──────────────────────────────────────────────────────────────────
 *
 * English and Puerto Rican Spanish are held in this file rather than in
 * `src/lib/i18n.ts`, which another work unit owns in this build — the same
 * arrangement `Intake.tsx`, `MapScreen.tsx` and `InspectionMode.tsx` already
 * use. Every table is typed so a missing translation is a compile error, which
 * is what the shared table's parity test achieves.
 *
 * ── §6, §7, §10.3 ────────────────────────────────────────────────────────────
 */

import config from '../../studio42.config.json'
import {
  centsToDisplay,
  formatRange,
  formatTriple,
  type Bps,
  type Cents,
  type Triple,
} from '../../shared/money'
import { businessDate, formatBusinessTime, type Timezone } from '../../shared/time'
import type { Certainty } from '../../shared/listing'
import type { AccessLevel } from '../../shared/access'
import type { Locale } from './i18n'

/** The business timezone, from the manifest. Never a literal in a screen. */
export const TZ: Timezone = config.timezone

/**
 * The BCP-47 tag `Intl` wants. `es-PR` is a real tag with Puerto Rican
 * grouping and month names, so it passes straight through; `en` becomes
 * `en-US` because a bare `en` gives British month order in some runtimes.
 */
const INTL: Record<Locale, string> = { en: 'en-US', 'es-PR': 'es-PR' }

export function intlLocale(locale: Locale): string {
  return INTL[locale] ?? INTL.en
}

/** The em dash we print where there is no value. Not a zero, and not blank. */
export const DASH = '—'

/* ────────────────────────────────────────────────────────────────────────────
 * Money
 *
 * Cents in, string out. No caller ever holds dollars.
 * ────────────────────────────────────────────────────────────────────────── */

export interface MoneyOptions {
  /**
   * Show the two decimal places. Default false.
   *
   * A card showing `$10,797` reads at a glance; the same card showing
   * `$10,796.75` claims a precision the estimate does not have. The cost
   * breakdown, where a column has to add up in front of a person, passes true.
   */
  showCents?: boolean
  /** What to print for a null. Defaults to the em dash. */
  dash?: string
}

export function money(cents: Cents | null | undefined, locale: Locale, options: MoneyOptions = {}): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return options.dash ?? DASH
  return centsToDisplay(cents, { locale: intlLocale(locale), showCents: options.showCents === true })
}

/** `"$9,048 – $12,785"` — the low and the high. What a card shows. */
export function range(t: Triple | null | undefined, locale: Locale, options: MoneyOptions = {}): string {
  if (!t) return options.dash ?? DASH
  return formatRange(t, { locale: intlLocale(locale), showCents: options.showCents === true })
}

/** `"$9,048 – $10,797 – $12,785"` — all three, for the breakdown screen. */
export function tripleRange(t: Triple | null | undefined, locale: Locale, options: MoneyOptions = {}): string {
  if (!t) return options.dash ?? DASH
  return formatTriple(t, { locale: intlLocale(locale), showCents: options.showCents === true })
}

/**
 * `"+$250"` / `"−$1,200"` — a change, with the sign kept.
 *
 * The minus is U+2212, not a hyphen: a price drop is a number, and a hyphen in
 * front of a currency symbol reads as a list bullet at 13px.
 */
export function delta(cents: Cents | null | undefined, locale: Locale, options: MoneyOptions = {}): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return options.dash ?? DASH
  const body = money(Math.abs(cents), locale, options)
  if (cents === 0) return body
  return cents > 0 ? `+${body}` : `−${body}`
}

/**
 * Basis points as a percentage. `1150` → `"11.5%"`.
 *
 * Trailing zeros are trimmed, so `1000` is `"10%"` and not `"10.0%"`.
 */
export function percentFromBps(points: Bps | null | undefined, locale: Locale): string {
  if (points === null || points === undefined || !Number.isFinite(points)) return DASH
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(points / 10_000)
}

/** A whole percentage a factor or a confidence already computed. `92` → `"92%"`. */
export function percent(value: number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  return new Intl.NumberFormat(intlLocale(locale), { style: 'percent', maximumFractionDigits: 0 }).format(value / 100)
}

/* ────────────────────────────────────────────────────────────────────────────
 * Time
 *
 * Two kinds of value arrive from the server and they are NOT interchangeable:
 *
 *  - an epoch millisecond number (`created_at`, `captured_at`, `last_seen_at`)
 *  - a `YYYY-MM-DD` business date (`decided_on`, `happened_at`, `due_on`,
 *    `verified_on`)
 *
 * A business date is already the answer to "which day" and must not be pushed
 * back through a timezone — `new Date('2026-09-04')` is midnight UTC, which is
 * 20:00 on the 3rd in Puerto Rico, and formatting it locally prints the wrong
 * day. So a date-only string is anchored at noon UTC and formatted in UTC,
 * where no zone can move it; an instant is formatted in the business zone.
 * ────────────────────────────────────────────────────────────────────────── */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** Today, where the land is. `YYYY-MM-DD`. */
export function today(now: number = Date.now()): string {
  return businessDate(TZ, now)
}

/** Noon UTC on a `YYYY-MM-DD`, which no timezone shift can move off the day. */
function noonOf(date: string): number | null {
  if (!ISO_DATE.test(date)) return null
  const ms = Date.parse(`${date}T12:00:00Z`)
  return Number.isFinite(ms) ? ms : null
}

/**
 * Whole days from one business date to another. Negative means `from` is later.
 *
 * Both ends are noon UTC, so the difference is exact whole days and no DST
 * transition anywhere can produce 0.958 of a day.
 */
export function dayDiff(from: string, to: string = today()): number | null {
  const a = noonOf(from)
  const b = noonOf(to)
  if (a === null || b === null) return null
  return Math.round((b - a) / 86_400_000)
}

export type DateStyle = 'short' | 'medium' | 'long'

const DATE_OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { day: 'numeric', month: 'short' },
  medium: { day: 'numeric', month: 'short', year: 'numeric' },
  long: { day: 'numeric', month: 'long', year: 'numeric' },
}

/**
 * A date a person reads. Accepts either shape; see the note above.
 *
 * `style: 'short'` drops the year, and is for a timeline where every row is
 * this year. Anything that could be years old uses `medium`.
 */
export function dateText(
  value: string | number | null | undefined,
  locale: Locale,
  style: DateStyle = 'medium',
): string {
  if (value === null || value === undefined || value === '') return DASH

  if (typeof value === 'string') {
    const ms = noonOf(value)
    if (ms === null) return value
    return new Intl.DateTimeFormat(intlLocale(locale), { ...DATE_OPTIONS[style], timeZone: 'UTC' }).format(ms)
  }

  if (!Number.isFinite(value)) return DASH
  return formatBusinessTime(TZ, value, intlLocale(locale), DATE_OPTIONS[style])
}

/** `"09:14"`, in the business timezone. Never the viewer's. */
export function timeText(at: number | null | undefined, locale: Locale): string {
  if (at === null || at === undefined || !Number.isFinite(at)) return DASH
  // `hourCycle: 'h23'` and not `hour12: false`: the latter yields "24:00" for
  // midnight in some runtimes, which shared/time.ts already had to normalise.
  return formatBusinessTime(TZ, at, intlLocale(locale), { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
}

/** `"4 Sep 2026, 09:14"` — for an audit line, where both halves matter. */
export function dateTimeText(at: number | null | undefined, locale: Locale): string {
  if (at === null || at === undefined || !Number.isFinite(at)) return DASH
  return `${dateText(at, locale)}, ${timeText(at, locale)}`
}

/**
 * `"3 days ago"`, `"yesterday"`, `"just now"`.
 *
 * Beyond a day the unit is the BUSINESS day, not 24-hour blocks: something
 * captured at 21:00 yesterday is "yesterday" at 08:00 this morning, not "11
 * hours ago", because the person reading it thinks in days.
 */
export function ago(at: number | null | undefined, locale: Locale, now: number = Date.now()): string {
  if (at === null || at === undefined || !Number.isFinite(at)) return DASH
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' })
  const seconds = Math.round((at - now) / 1000)

  if (Math.abs(seconds) < 45) return COPY[locale].justNow
  if (Math.abs(seconds) < 3600) return rtf.format(Math.round(seconds / 60), 'minute')

  const days = dayDiff(businessDate(TZ, at), businessDate(TZ, now))
  if (days === null) return DASH
  if (days === 0) return rtf.format(Math.round(seconds / 3600), 'hour')
  if (Math.abs(days) < 45) return rtf.format(-days, 'day')
  if (Math.abs(days) < 365) return rtf.format(-Math.round(days / 30), 'month')
  return rtf.format(-Math.round(days / 365), 'year')
}

/** Is this business date in the past? A due date, against today where the land is. */
export function isOverdue(dueOn: string | null | undefined, now: number = Date.now()): boolean {
  if (!dueOn) return false
  const days = dayDiff(dueOn, today(now))
  return days !== null && days > 0
}

/**
 * `"Due today"`, `"Due tomorrow"`, `"3 days overdue"`.
 *
 * Overdue is worded rather than signed, because "in −3 days" is what a
 * relative formatter produces and nobody reads that as late.
 */
export function due(dueOn: string | null | undefined, locale: Locale, now: number = Date.now()): string {
  if (!dueOn) return ''
  const days = dayDiff(today(now), dueOn)
  if (days === null) return ''
  const c = COPY[locale]
  if (days < 0) {
    const late = Math.abs(days)
    return late === 1 ? c.overdueOne : c.overdueMany.replace('{n}', String(late))
  }
  if (days === 0) return c.dueToday
  if (days === 1) return c.dueTomorrow
  return c.dueInDays.replace('{n}', String(days))
}

/* ────────────────────────────────────────────────────────────────────────────
 * Measurement
 *
 * Feet and pounds, because that is what a tape measure and a hitch weight are
 * quoted in here; kilometres, because that is what the haversine in
 * `src/lib/geo.ts` returns and converting it twice is how the two disagree.
 * ────────────────────────────────────────────────────────────────────────── */

/** `8.0 ft` — one decimal normally, two when the value needs it. Matches shared/access.ts. */
export function feet(value: number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  const rounded = Math.round(value * 100) / 100
  const digits = Number.isInteger(rounded * 10) ? 1 : 2
  const n = new Intl.NumberFormat(intlLocale(locale), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(rounded)
  return `${n} ${COPY[locale].unitFt}`
}

export function pounds(value: number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  return `${new Intl.NumberFormat(intlLocale(locale)).format(Math.round(value))} ${COPY[locale].unitLb}`
}

/**
 * `"22 km"`. Under 10 km keeps one decimal — the difference between 4.0 and
 * 4.9 km of towing is real money on a per-mile rate.
 */
export function km(value: number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  const digits = Math.abs(value) < 10 ? 1 : 0
  const n = new Intl.NumberFormat(intlLocale(locale), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)
  return `${n} km`
}

/** `"12%"` from a whole-number percentage — a slope, a variance, a coverage. */
export function pct(value: number | null | undefined, locale: Locale, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  const n = new Intl.NumberFormat(intlLocale(locale), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value)
  return `${n}%`
}

/** A plain count, grouped. `1200` → `"1,200"`. */
export function count(value: number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  return new Intl.NumberFormat(intlLocale(locale)).format(value)
}

/* ────────────────────────────────────────────────────────────────────────────
 * Tone
 *
 * A pair of CSS custom properties, never a hex literal. `index.css` defines
 * both themes; a component that hard-codes `#0f7a4d` is invisible in the dark
 * one, and there is no halfway retrofit for that either.
 * ────────────────────────────────────────────────────────────────────────── */

export interface Tone {
  /** Text colour. */
  fg: string
  /** Chip background. */
  bg: string
  /** Border, for the badges that must not read as filled. */
  line: string
}

const tone = (fg: string, bg: string, line = 'transparent'): Tone => ({ fg, bg, line })

export const TONES = {
  strong: tone('var(--score-strong)', 'var(--score-strong-bg)'),
  fair: tone('var(--score-fair)', 'var(--score-fair-bg)'),
  weak: tone('var(--score-weak)', 'var(--score-weak-bg)'),
  money: tone('var(--money)', 'var(--money-bg)'),
  danger: tone('var(--danger)', 'var(--warn-bg)', 'var(--danger)'),
  warn: tone('var(--warn)', 'var(--warn-bg)'),
  brand: tone('var(--brand-strong)', 'var(--brand-soft)'),
  quiet: tone('var(--muted)', 'var(--surface)', 'var(--line)'),
} satisfies Record<string, Tone>

/* ────────────────────────────────────────────────────────────────────────────
 * The Homestead Score
 * ────────────────────────────────────────────────────────────────────────── */

export type ScoreBand = 'strong' | 'fair' | 'weak' | 'unscored'

/**
 * Bands for COLOUR only — they are not thresholds anything decides on.
 *
 * The alert threshold is `homestead.alert.min_score` and lives in settings; a
 * band hard-coded here that happened to line up with it would be a second,
 * silent copy of a rule the server owns (CLAUDE.md rule 14).
 */
export function scoreBand(value: number | null | undefined): ScoreBand {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'unscored'
  if (value >= 75) return 'strong'
  if (value >= 50) return 'fair'
  return 'weak'
}

export function scoreTone(value: number | null | undefined): Tone {
  const band = scoreBand(value)
  return band === 'strong' ? TONES.strong : band === 'fair' ? TONES.fair : TONES.weak
}

/** The bare number, or the em dash. Never `0` for "not scored yet". */
export function score(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH
  return String(Math.round(value))
}

/**
 * §6.2: the UI never shows the bare number.
 *
 * `"Homestead Score 92 · confidence 100% — 13 of 13 factors have data."`
 *
 * Renormalising over applicable factors lets an opportunity nobody knows
 * anything about score very high, because only the factors that happen to look
 * good are in the denominator. The counterweight is only a counterweight if it
 * is on the screen next to the number.
 */
export function scoreLine(
  args: { score: number | null; confidence: number | null; withData: number; total: number },
  locale: Locale,
): string {
  const c = COPY[locale]
  if (args.score === null || args.score === undefined) return c.notScored
  const head = `${c.scoreName} ${score(args.score)}`
  if (args.confidence === null || args.total === 0) return head
  return `${head} · ${c.confidence} ${percent(args.confidence, locale)} — ${c.factorsHaveData
    .replace('{n}', String(args.withData))
    .replace('{total}', String(args.total))}`
}

/**
 * The risk cap, said out loud.
 *
 * `raw_score` is preserved precisely so this sentence can exist: clearing the
 * flag restores the real number, and a capped score with no explanation is a
 * number that moved for no visible reason.
 */
export function cappedBy(rawScore: number | null, cappedScore: number | null, locale: Locale): string | null {
  if (rawScore === null || cappedScore === null) return null
  if (rawScore <= cappedScore) return null
  return COPY[locale].cappedBy.replace('{raw}', score(rawScore)).replace('{score}', score(cappedScore))
}

/* ────────────────────────────────────────────────────────────────────────────
 * Cost confidence — §7.4. The band leads, the number follows.
 * ────────────────────────────────────────────────────────────────────────── */

export type CostBand = 'FIRM' | 'INDICATIVE' | 'ROUGH'

export function costBandLabel(band: CostBand, locale: Locale): string {
  return COPY[locale][`band${band}` as const]
}

export function costBandTone(band: CostBand): Tone {
  return band === 'FIRM' ? TONES.strong : band === 'INDICATIVE' ? TONES.fair : TONES.quiet
}

/**
 * The word every unverified number wears: **assumed**.
 *
 * Every cost assumption ships `verified_on = NULL`, so a first estimate lands
 * honestly at ROUGH or INDICATIVE. That has to be visible on the line, not
 * only in a band at the top of the panel — the line is what somebody quotes.
 */
export function assumed(locale: Locale): string {
  return COPY[locale].assumed
}

/** The three readings, named. §7.4: `expected` is the one to plan against. */
export function readingLabel(reading: 'low' | 'expected' | 'high', locale: Locale): string {
  return COPY[locale][`reading_${reading}` as const]
}

export function readingHint(reading: 'low' | 'expected' | 'high', locale: Locale): string {
  return COPY[locale][`readingHint_${reading}` as const]
}

/* ────────────────────────────────────────────────────────────────────────────
 * The certainty badges
 *
 * Six categories, and the one rule that matters: **an AI inference must never
 * look like a confirmed fact.** `AI_INFERENCE` therefore gets its own tone,
 * its own outline and its own word — it does not collapse into OUR ESTIMATE,
 * which is exactly what a rank-ordered provenance would let it do.
 * ────────────────────────────────────────────────────────────────────────── */

export interface Badge {
  /** The certainty itself, for a `data-` attribute or a test. */
  code: Certainty
  /** Two or three words, upper case, compact enough for a chip. */
  label: string
  tone: Tone
  /** One sentence, for the `title` and the legend. */
  hint: string
  /** Drawn as an outline rather than a fill. The estimates and the unknowns. */
  outlined: boolean
}

const CERTAINTY_TONE: Record<Certainty, Tone> = {
  VERIFIED: TONES.strong,
  SOURCE_FACT: TONES.money,
  SELLER_CLAIM: TONES.fair,
  OUR_ESTIMATE: TONES.quiet,
  AI_INFERENCE: TONES.danger,
  UNKNOWN: TONES.quiet,
}

/** Filled reads as settled. Only the two certainties that ARE settled are filled. */
const CERTAINTY_FILLED: Record<Certainty, boolean> = {
  VERIFIED: true,
  SOURCE_FACT: true,
  SELLER_CLAIM: true,
  OUR_ESTIMATE: false,
  AI_INFERENCE: false,
  UNKNOWN: false,
}

export function certaintyBadge(certainty: Certainty, locale: Locale): Badge {
  return {
    code: certainty,
    label: COPY[locale][`certainty_${certainty}` as const],
    tone: CERTAINTY_TONE[certainty],
    hint: COPY[locale][`certaintyHint_${certainty}` as const],
    outlined: !CERTAINTY_FILLED[certainty],
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Access — §10.2's roll-up. Grey is NOT FULLY VERIFIED, and it is not green.
 * ────────────────────────────────────────────────────────────────────────── */

export function accessLabel(level: AccessLevel, locale: Locale): string {
  return COPY[locale][`access_${level}` as const]
}

export function accessTone(level: AccessLevel): Tone {
  switch (level) {
    case 'red':
      return TONES.danger
    case 'amber':
      return TONES.warn
    case 'green':
      return TONES.strong
    default:
      return TONES.quiet
  }
}

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  justNow: 'just now',
  dueToday: 'Due today',
  dueTomorrow: 'Due tomorrow',
  dueInDays: 'Due in {n} days',
  overdueOne: '1 day overdue',
  overdueMany: '{n} days overdue',

  unitFt: 'ft',
  unitLb: 'lb',

  scoreName: 'Homestead Score',
  confidence: 'confidence',
  factorsHaveData: '{n} of {total} factors have data',
  notScored: 'Not scored yet',
  cappedBy: 'Capped at {score} by an open risk flag — it scores {raw} without it.',

  bandFIRM: 'FIRM',
  bandINDICATIVE: 'INDICATIVE',
  bandROUGH: 'ROUGH',
  assumed: 'assumed',

  reading_low: 'Low',
  reading_expected: 'Expected',
  reading_high: 'High',
  readingHint_low: 'If everything goes right.',
  readingHint_expected: 'Plan against this.',
  readingHint_high: 'If everything goes wrong.',

  certainty_VERIFIED: 'VERIFIED',
  certainty_SOURCE_FACT: 'SOURCE FACT',
  certainty_SELLER_CLAIM: 'SELLER CLAIM',
  certainty_OUR_ESTIMATE: 'OUR ESTIMATE',
  certainty_AI_INFERENCE: 'AI INFERENCE',
  certainty_UNKNOWN: 'UNKNOWN',
  certaintyHint_VERIFIED: 'Seen, photographed or documented by us.',
  certaintyHint_SOURCE_FACT: 'Printed in the listing itself.',
  certaintyHint_SELLER_CLAIM: 'The seller said so. Nobody has checked it.',
  certaintyHint_OUR_ESTIMATE: 'We worked it out. It is an estimate, not a measurement.',
  certaintyHint_AI_INFERENCE: 'A model guessed this from the listing text. Treat it as a lead, never as a fact.',
  certaintyHint_UNKNOWN: 'Nobody has answered this yet.',

  access_red: 'ACCESS WARNING',
  access_amber: 'TIGHT',
  access_grey: 'NOT FULLY VERIFIED',
  access_green: 'FITS',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  justNow: 'ahora mismo',
  dueToday: 'Para hoy',
  dueTomorrow: 'Para mañana',
  dueInDays: 'En {n} días',
  overdueOne: '1 día tarde',
  overdueMany: '{n} días tarde',

  unitFt: 'pies',
  unitLb: 'lb',

  scoreName: 'Puntuación Homestead',
  confidence: 'confianza',
  factorsHaveData: '{n} de {total} factores tienen datos',
  notScored: 'Todavía sin puntuar',
  cappedBy: 'Limitada a {score} por una alerta de riesgo abierta — sin ella saca {raw}.',

  bandFIRM: 'FIRME',
  bandINDICATIVE: 'INDICATIVO',
  bandROUGH: 'APROXIMADO',
  assumed: 'supuesto',

  reading_low: 'Bajo',
  reading_expected: 'Esperado',
  reading_high: 'Alto',
  readingHint_low: 'Si todo sale bien.',
  readingHint_expected: 'Planifica con este.',
  readingHint_high: 'Si todo sale mal.',

  certainty_VERIFIED: 'VERIFICADO',
  certainty_SOURCE_FACT: 'DATO DEL ANUNCIO',
  certainty_SELLER_CLAIM: 'DICE EL VENDEDOR',
  certainty_OUR_ESTIMATE: 'ESTIMADO NUESTRO',
  certainty_AI_INFERENCE: 'INFERENCIA DE IA',
  certainty_UNKNOWN: 'SIN DATO',
  certaintyHint_VERIFIED: 'Lo vimos, lo fotografiamos o está en un documento.',
  certaintyHint_SOURCE_FACT: 'Está escrito en el anuncio.',
  certaintyHint_SELLER_CLAIM: 'Lo dijo el vendedor. Nadie lo ha comprobado.',
  certaintyHint_OUR_ESTIMATE: 'Lo calculamos nosotros. Es un estimado, no una medida.',
  certaintyHint_AI_INFERENCE: 'Un modelo lo dedujo del texto del anuncio. Es una pista, nunca un hecho.',
  certaintyHint_UNKNOWN: 'Nadie ha contestado esto todavía.',

  access_red: 'AVISO DE ACCESO',
  access_amber: 'JUSTO',
  access_grey: 'NO VERIFICADO DEL TODO',
  access_green: 'CABE',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }
